import React, { useState } from 'react';
import { X } from 'lucide-react';

interface ShowcaseProps {
  onLaunchPlatform?: () => void;
}

const Showcase: React.FC<ShowcaseProps> = ({ onLaunchPlatform }) => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [isGlitching, setIsGlitching] = useState(false);

  // Community art images with metadata
  const artGallery = [
    {
      url: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/19888.png',
      title: 'Digital Warrior',
      artist: '@EKKOArt_1'
    },
    {
      url: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/889.png',
      title: 'Neon Samurai',
      artist: '@CryptoDesigner'
    },
    {
      url: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/9999.png',
      title: 'Future Vision',
      artist: '@BlockchainArt'
    },
    {
      url: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/1891.png',
      title: 'Cyber Guardian',
      artist: '@PixelMaster'
    },
    {
      url: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/216.png',
      title: 'Electric Dreams',
      artist: '@NFTCreator'
    },
    {
      url: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/212.png',
      title: 'Tech Warrior',
      artist: '@DigitalArt'
    },
    {
      url: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/team.png',
      title: 'EKKO Heroes',
      artist: '@TeamEKKO'
    },
    {
      url: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/19888.png',
      title: 'Quantum Blade',
      artist: '@CommunityArt'
    },
    {
      url: 'https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/889.png',
      title: 'Void Walker',
      artist: '@ArtistCollective'
    }
  ];

  const handleImageClick = (index: number) => {
    setIsGlitching(true);
    setTimeout(() => {
      setSelectedImage(index);
      setIsGlitching(false);
    }, 200);
  };

  const closeFullsize = () => {
    setIsGlitching(true);
    setTimeout(() => {
      setSelectedImage(null);
      setIsGlitching(false);
    }, 200);
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Architectural Lines */}
      <div className="fixed inset-0 pointer-events-none z-5">
        {/* Horizontal lines */}
        <div className="absolute top-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#24c35b] to-transparent"></div>
        <div className="absolute bottom-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        
        {/* Vertical lines */}
        <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-0 right-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        
        {/* Cross decorations */}
        <div className="absolute top-32 left-1/4 w-8 h-8 transform -translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        <div className="absolute top-32 right-1/4 w-8 h-8 transform translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        
        {/* Corner plus decorations */}
        <div className="absolute top-8 left-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute top-8 right-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute bottom-8 left-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute bottom-8 right-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
      </div>

      {/* Background Text */}
      <div className="fixed inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
        <div className="text-gray-200 font-black select-none text-center space-y-8">
          <div className="text-[8vw] opacity-20 leading-none">ART</div>
          <div className="text-[10vw] opacity-15 leading-none">GALLERY</div>
          <div className="text-[6vw] opacity-10 leading-none">COMMUNITY</div>
        </div>
      </div>

      {/* Fullsize Image Viewer */}
      {selectedImage !== null && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          <div className={`relative max-w-5xl w-full ${isGlitching ? 'animate-pulse' : ''}`}>
            {/* Close button */}
            <button
              onClick={closeFullsize}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-[#24c35b] hover:bg-[#1eaa4e] rounded-full flex items-center justify-center text-white transition-colors shadow-lg"
            >
              <X size={20} />
            </button>
            
            {/* Fullsize image with glitch effect */}
            <div className={`relative ${isGlitching ? 'animate-ping' : ''}`}>
              <img
                src={artGallery[selectedImage].url}
                alt={artGallery[selectedImage].title}
                className={`w-full h-auto max-h-[80vh] object-contain rounded-xl shadow-2xl ${
                  isGlitching ? 'filter hue-rotate-180 saturate-200' : ''
                }`}
              />
              
              {/* Glitch overlay */}
              {isGlitching && (
                <div className="absolute inset-0 bg-gradient-to-r from-[#24c35b]/30 via-transparent to-[#24c35b]/30 mix-blend-multiply rounded-xl"></div>
              )}
            </div>
            
            {/* Image info */}
            <div className="mt-6 text-center">
              <h3 className="text-2xl font-bold text-[#24c35b] mb-2">
                {artGallery[selectedImage].title}
              </h3>
              <div className="flex items-center justify-center gap-2">
                <span className="text-white text-sm">Made by</span>
                <div className="flex items-center gap-1 bg-black/50 px-3 py-1 rounded-full">
                  {/* Official X Logo */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-white">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  <span className="text-white text-sm font-medium">{artGallery[selectedImage].artist}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <section className="relative z-10 min-h-screen py-20">
        <div className="container mx-auto px-4 max-w-7xl">
          
          {/* Hero Title - Centered */}
          <div className="mb-16 pt-8 text-center">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-5xl lg:text-6xl font-black leading-tight mb-6">
                <span className="bg-gradient-to-r from-[#24c35b] via-[#1eaa4e] to-[#24c35b] bg-clip-text text-transparent">
                  Gei no Chi
                </span>
                <br />
                <span className="text-gray-900">Land of Art</span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Showcasing the highest rated community EKKO creations.
              </p>
            </div>
          </div>

          {/* 3x3 Art Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {artGallery.map((artwork, index) => (
              <div 
                key={index} 
                className="relative group cursor-pointer transform hover:scale-105 transition-all duration-500"
                onClick={() => handleImageClick(index)}
              >
                {/* Architectural art card styling */}
                <div className="relative arch-card rounded-2xl overflow-hidden aspect-square shadow-2xl hover:shadow-3xl transition-all duration-500">
                  {/* Architectural corner lines */}
                  <div className="absolute top-0 left-0 w-8 h-8 z-20">
                    <div className="absolute top-0 left-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-0 left-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="absolute top-0 right-0 w-8 h-8 z-20">
                    <div className="absolute top-0 right-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-0 right-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="absolute bottom-0 left-0 w-8 h-8 z-20">
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-0 left-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="absolute bottom-0 right-0 w-8 h-8 z-20">
                    <div className="absolute bottom-0 right-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-0 right-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Art Image - Fills entire card with no gaps */}
                  <img 
                    src={artwork.url}
                    alt={artwork.title}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
                  />

                  {/* Overlay with art info */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="text-white">
                        {/* Green title */}
                        <h3 className="text-lg font-bold mb-2 text-[#24c35b]">{artwork.title}</h3>
                        
                        {/* Made by with X username */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-sm text-gray-300">Made by</span>
                          <div className="flex items-center gap-1 bg-black/50 px-2 py-1 rounded-full">
                            {/* Official X Logo */}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-white">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                            <span className="text-xs text-white font-medium">{artwork.artist}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <div key={i} className="w-4 h-4 text-[#24c35b] fill-current">
                                ⭐
                              </div>
                            ))}
                          </div>
                          <span className="text-xs text-gray-400">Community Favorite</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Architectural detail line */}
                  <div className="absolute bottom-6 left-6 right-6 h-0.5 bg-gradient-to-r from-transparent via-[#24c35b]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-15"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Call to Action */}
          <div className="text-center mt-20">
            <div className="relative arch-card rounded-3xl p-12 max-w-4xl mx-auto overflow-hidden">
              {/* Architectural corner decorations */}
              <div className="absolute top-0 left-0 w-8 h-8">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                <div className="absolute top-0 left-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
              </div>
              <div className="absolute top-0 right-0 w-8 h-8">
                <div className="absolute top-0 right-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                <div className="absolute top-0 right-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
              </div>
              <div className="absolute bottom-0 left-0 w-8 h-8">
                <div className="absolute bottom-0 left-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                <div className="absolute bottom-0 left-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
              </div>
              <div className="absolute bottom-0 right-0 w-8 h-8">
                <div className="absolute bottom-0 right-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                <div className="absolute bottom-0 right-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
              </div>

              <div className="relative z-10">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#24c35b] to-emerald-400 flex items-center justify-center text-white shadow-xl">
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Join the <span className="text-gradient">Creative Community</span>
                </h3>
                <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                  Share your EKKO-inspired creations and become part of our growing artist community.
                </p>
                
                <button 
                  onClick={onLaunchPlatform}
                  className="btn-primary px-8 py-4 text-lg font-bold rounded-xl shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 group mx-auto"
                >
                  <svg className="h-5 w-5 group-hover:translate-x-1 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span>Launch Platform</span>
                </button>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Gallery Stats - Bottom */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="arch-card px-8 py-4 rounded-xl border border-white/20 flex items-center gap-8">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-[#24c35b]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-gray-600">9 Featured Artworks</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-[#24c35b]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-gray-600">Community Curated</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-[#24c35b]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="text-sm text-gray-600">Highest Rated</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Showcase; 